import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getModelById,
  getModelBySlug,
  getPublicModels,
  getModelsByUsername,
  createModel,
  updateModel,
  deleteModel,
  forkModel,
  incrementViewCount,
  uploadThumbnail,
  type CreateModelInput,
  type ModelFileInput,
} from '@/services/models';
import { getVersionsByModelId, restoreVersion } from '@/services/versions';
import type { ModelVersion } from '@/lib/supabase/types';

// ============================================
// QUERY KEYS
// ============================================

export const modelKeys = {
  all: ['models'] as const,
  lists: () => [...modelKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...modelKeys.lists(), filters] as const,
  details: () => [...modelKeys.all, 'detail'] as const,
  detail: (id: string) => [...modelKeys.details(), id] as const,
  bySlug: (username: string, slug: string) =>
    [...modelKeys.all, 'slug', username, slug] as const,
  byUser: (username: string) => [...modelKeys.all, 'user', username] as const,
  versions: (modelId: string) =>
    [...modelKeys.detail(modelId), 'versions'] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Hook to fetch a model by ID
 */
export function useModel(modelId: string | null) {
  return useQuery({
    queryKey: modelKeys.detail(modelId || ''),
    queryFn: () => (modelId ? getModelById(modelId) : null),
    enabled: !!modelId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch a model by username and slug
 */
export function useModelBySlug(username: string | null, slug: string | null) {
  return useQuery({
    queryKey: modelKeys.bySlug(username || '', slug || ''),
    queryFn: () => (username && slug ? getModelBySlug(username, slug) : null),
    enabled: !!username && !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch public models with pagination and filters
 */
export function usePublicModels(options?: {
  limit?: number;
  offset?: number;
  sortBy?: 'newest' | 'popular' | 'most-forked';
  search?: string;
}) {
  return useQuery({
    queryKey: modelKeys.list(options || {}),
    queryFn: () => getPublicModels(options),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch models by username
 */
export function useUserModels(
  username: string | null,
  options?: {
    includePrivate?: boolean;
  },
) {
  return useQuery({
    queryKey: modelKeys.byUser(username || ''),
    queryFn: () => (username ? getModelsByUsername(username, options) : []),
    enabled: !!username,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch version history for a model
 */
export function useModelVersions(modelId: string | null) {
  return useQuery({
    queryKey: modelKeys.versions(modelId || ''),
    queryFn: () => (modelId ? getVersionsByModelId(modelId) : []),
    enabled: !!modelId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Hook to create a new model
 */
export function useCreateModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      input,
    }: {
      userId: string;
      input: CreateModelInput;
    }) => createModel(userId, input),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: modelKeys.byUser(data.author.username),
      });
      // Set the new model in cache
      queryClient.setQueryData(modelKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to update a model
 */
export function useUpdateModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      modelId,
      input,
    }: {
      modelId: string;
      input: {
        title?: string;
        description?: string;
        scadCode?: string;
        isPublic?: boolean;
        commitMessage?: string;
        files?: ModelFileInput[];
        downloadPrice?: number | null;
      };
    }) => updateModel(modelId, input),
    onSuccess: (data) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: modelKeys.versions(data.id) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
  });
}

/**
 * Hook to delete a model (soft delete)
 */
export function useDeleteModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modelId: string) => deleteModel(modelId),
    onSuccess: (_data, modelId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: modelKeys.detail(modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
  });
}

/**
 * Hook to fork a model
 */
export function useForkModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      originalModelId,
      newTitle,
    }: {
      userId: string;
      originalModelId: string;
      newTitle?: string;
    }) => forkModel(userId, originalModelId, newTitle),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: modelKeys.byUser(data.author.username),
      });
      queryClient.setQueryData(modelKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to restore a model to a previous version
 */
export function useRestoreVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      modelId,
      version,
    }: {
      modelId: string;
      version: ModelVersion;
    }) => restoreVersion(modelId, version),
    onSuccess: (_data, { modelId }) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.versions(modelId) });
    },
  });
}

/**
 * Hook to upload a thumbnail
 */
export function useUploadThumbnail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      modelId,
      thumbnailBlob,
    }: {
      modelId: string;
      thumbnailBlob: Blob;
    }) => uploadThumbnail(modelId, thumbnailBlob),
    onSuccess: (_data, { modelId }) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
  });
}

/**
 * Hook to track view (doesn't need to be reactive)
 */
export function useTrackView() {
  return useMutation({
    mutationFn: (modelId: string) => incrementViewCount(modelId),
  });
}
